import CalculateIcon from '@mui/icons-material/Calculate';
import PostAddIcon from '@mui/icons-material/PostAdd';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FormField, FormGrid, InlineFeedback, PrimaryButton } from 'components/forms';
import type React from 'react';
import { useState } from 'react';
import { useGetInsurexSaleOptionsQuery, useSimulateClientSaleMutation } from 'services/api/clients.api';
import priceFormat from 'services/format/priceFormat';
import type { InsuranceType } from 'services/types/sale';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import { emitSaleCreatePrefill } from './saleCreatePrefillBus';

interface SimulatorSectionProps {
  clientId: number;
}

interface FormState {
  institutionName: string;
  totalAskedAmount: string;
  pendingBalance: string;
  remainingQuotas: string;
  installmentValue: string;
}

const INITIAL_STATE: FormState = {
  institutionName: '',
  totalAskedAmount: '',
  pendingBalance: '',
  remainingQuotas: '',
  installmentValue: '',
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const FIELD_LABELS: Record<keyof FormState, string> = {
  institutionName: 'Institución',
  totalAskedAmount: 'Monto Total Solicitado',
  pendingBalance: 'Saldo Pendiente',
  remainingQuotas: 'Cuotas Restantes',
  installmentValue: 'Valor de la Cuota',
};

const parsePositiveNumber = (v: string): number | null => {
  const n = Number(v.trim());
  return Number.isFinite(n) && n > 0 ? n : null;
};

const FIELD_MINIMUMS: Partial<Record<keyof FormState, number>> = {
  totalAskedAmount: 1_500_000,
  pendingBalance: 1_000_000,
  remainingQuotas: 6,
};

const formatMinimum = (key: keyof FormState, min: number): string =>
  key === 'remainingQuotas' ? String(min) : `$${min.toLocaleString('es-CL')}`;

const validateRequiredPositive = (key: keyof FormState, value: string): string | undefined => {
  if (key === 'installmentValue') return undefined;
  if (key === 'institutionName') {
    return value.trim() === '' ? 'Selecciona una institución' : undefined;
  }
  if (value.trim() === '') return 'Ingresa un valor';
  const parsed = parsePositiveNumber(value);
  if (parsed == null) return 'Debe ser un número mayor a 0';
  const min = FIELD_MINIMUMS[key];
  if (min != null && parsed < min) return `Debe ser al menos ${formatMinimum(key, min)}`;
  return undefined;
};

const SimulatorSection: React.FC<SimulatorSectionProps> = ({ clientId }) => {
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    loanProtectionInsurance: number | null;
    unemploymentInsurance: number | null;
    usedFallbackInstitution?: boolean;
    simulatedInstitutionSlug?: string;
  } | null>(null);
  const [simulate, { isLoading }] = useSimulateClientSaleMutation();
  const { data: saleOptions } = useGetInsurexSaleOptionsQuery(undefined);
  const loanInstitutions = saleOptions?.loanInstitutions ?? [];

  const setField =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as FormState[K];
      setState((prev) => ({ ...prev, [key]: value }));
      if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const handleBlur = (key: keyof FormState) => () => {
    const value = state[key];
    if (value.trim() === '' && key !== 'institutionName') return;
    const error = validateRequiredPositive(key, value);
    setFieldErrors((prev) => ({ ...prev, [key]: error }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError(null);
    setResult(null);

    const errors: FieldErrors = {};
    (Object.keys(state) as (keyof FormState)[]).forEach((key) => {
      const err = validateRequiredPositive(key, state[key]);
      if (err) errors[key] = err;
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const missing = Object.keys(errors)
        .map((k) => FIELD_LABELS[k as keyof FormState])
        .join(', ');
      setServerError(`Revisa estos campos antes de simular: ${missing}.`);
      return;
    }

    try {
      const data = await simulate({
        clientId: String(clientId),
        body: {
          institutionName: state.institutionName.trim(),
          totalAskedAmount: parsePositiveNumber(state.totalAskedAmount) as number,
          pendingBalance: parsePositiveNumber(state.pendingBalance) as number,
          remainingQuotas: Math.trunc(parsePositiveNumber(state.remainingQuotas) as number),
          currency: 'CLP',
        },
      }).unwrap();
      setResult(data);
    } catch {
      setServerError('Ocurrió un error al ejecutar la simulación. Intenta más tarde.');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        sx={{
          color: alpha(NAVY, 0.6),
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.875rem',
          mb: 2.5,
        }}
      >
        Ingresa los datos del crédito y simulamos el reembolso con la API interna de Insurex.
      </Typography>

      <FormGrid columns={2} gap={2.5} sx={{ mb: 2.5 }}>
        <FormField
          select
          required
          label="Institución"
          value={state.institutionName}
          onChange={setField('institutionName')}
          onBlur={handleBlur('institutionName')}
          error={fieldErrors.institutionName ?? null}
        >
          <MenuItem value="" disabled sx={{ fontFamily: 'Jost, sans-serif' }}>
            <em>{loanInstitutions.length === 0 ? 'Cargando…' : 'Selecciona una institución'}</em>
          </MenuItem>
          {loanInstitutions.map((name) => (
            <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
              {name}
            </MenuItem>
          ))}
        </FormField>
        <FormField
          required
          thousandSeparator
          label="Monto Total Solicitado (CLP)"
          value={state.totalAskedAmount}
          onChange={setField('totalAskedAmount')}
          onBlur={handleBlur('totalAskedAmount')}
          error={fieldErrors.totalAskedAmount ?? null}
        />
        <FormField
          required
          thousandSeparator
          label="Saldo Pendiente (CLP)"
          value={state.pendingBalance}
          onChange={setField('pendingBalance')}
          onBlur={handleBlur('pendingBalance')}
          error={fieldErrors.pendingBalance ?? null}
        />
        <FormField
          required
          type="number"
          label="Cuotas Restantes"
          value={state.remainingQuotas}
          onChange={setField('remainingQuotas')}
          onBlur={handleBlur('remainingQuotas')}
          error={fieldErrors.remainingQuotas ?? null}
        />
        <FormField
          optional
          thousandSeparator
          label="Valor de la Cuota (CLP)"
          value={state.installmentValue}
          onChange={setField('installmentValue')}
        />
      </FormGrid>

      {serverError && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          {serverError}
        </InlineFeedback>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <PrimaryButton type="submit" disabled={isLoading} startIcon={<CalculateIcon fontSize="small" />}>
          {isLoading ? 'Simulando…' : 'Simular'}
        </PrimaryButton>
      </Box>

      {result && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: RADIUS_CARD,
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            boxShadow: SHADOW,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              mb: 1.25,
              color: NAVY,
            }}
          >
            Resultado de la simulación
          </Typography>
          {result.usedFallbackInstitution && (
            <InlineFeedback severity="warning" sx={{ mb: 1.5 }}>
              {`La institución "${state.institutionName}" no existe en el simulador de Insurex. Se simuló como "otros" — los montos pueden no reflejar la institución real.`}
            </InlineFeedback>
          )}
          <FormGrid columns={2} gap={1.5}>
            {[
              {
                type: 'loanProtectionInsurance' as InsuranceType,
                label: 'Desgravamen',
                amount: result.loanProtectionInsurance,
              },
              {
                type: 'unemploymentInsurance' as InsuranceType,
                label: 'Cesantía',
                amount: result.unemploymentInsurance,
              },
            ].map(({ type, label, amount }) => {
              const canPrefill = amount != null && amount > 0;
              const handlePrefill = () => {
                emitSaleCreatePrefill({
                  clientId,
                  insuranceType: type,
                  institutionName: state.institutionName,
                  totalAskedAmount: state.totalAskedAmount,
                  pendingBalance: state.pendingBalance,
                  remainingQuotas: state.remainingQuotas,
                  installmentValue: state.installmentValue,
                  simulationResult: amount,
                });
              };
              return (
                <Box key={type} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.75rem',
                        color: alpha(NAVY, 0.6),
                        display: 'block',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        color: NAVY,
                        fontSize: '0.9375rem',
                      }}
                    >
                      {amount != null ? priceFormat.format(amount) : '—'}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      canPrefill
                        ? `Completar con el valor para el seguro de ${label.toLowerCase()}`
                        : 'Resultado no válido para crear portabilidad'
                    }
                    arrow
                  >
                    <span>
                      <IconButton size="small" onClick={handlePrefill} disabled={!canPrefill} sx={{ color: NAVY }}>
                        <PostAddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </FormGrid>
        </Box>
      )}
    </Box>
  );
};

export default SimulatorSection;
